import React from "react";
import { useForm } from "react-hook-form";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";
import { ArrowLeft } from "lucide-react";

const ForgotPasswordPage = () => {
  const { locketId: urlLocketId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset: resetForm,
    watch,
  } = useForm({
    defaultValues: {
      locketId: urlLocketId || "",
    },
  });
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [resetMethod, setResetMethod] = React.useState("email");
  const [smsAvailable, setSmsAvailable] = React.useState(false);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpCountdown, setOtpCountdown] = React.useState(0);
  const countdownRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/qr/sms-available")
      .then((res) => setSmsAvailable(res.data?.smsAvailable === true))
      .catch(() => setSmsAvailable(false));
  }, []);

  const startCountdown = () => {
    setOtpCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  React.useEffect(() => () => { if (countdownRef.current) clearInterval(countdownRef.current); }, []);

  const onSubmitEmail = async (data) => {
    setError(null);
    setSuccessMessage(null);
    try {
      await api.post("/qr/forgot-password", {
        email: data.email,
        locketId: (urlLocketId || data.locketId)?.toUpperCase().trim(),
      });
      setSuccessMessage(
        "If an account exists with this email and QR Code ID, you will receive a password reset link shortly. Please check your inbox."
      );
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send reset link. Please try again.");
    }
  };

  const onSubmitPhoneOtp = async (data) => {
    setError(null);
    setSuccessMessage(null);
    try {
      if (!otpSent) {
        await api.post("/qr/forgot-password-phone", { phone: data.phone });
        setOtpSent(true);
        startCountdown();
      } else {
        await api.post("/qr/reset-password", {
          identifier: data.phone,
          otp: data.otp,
          newPassword: data.newPassword,
        });
        setSuccessMessage("Password reset successfully! You can now log in with your new password.");
        setOtpSent(false);
        resetForm();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed. Please try again.");
    }
  };

  const handleResendOtp = async () => {
    if (otpCountdown > 0) return;
    setError(null);
    const phone = watch("phone");
    if (!phone) { setError("Please enter your phone number first."); return; }
    try {
      await api.post("/qr/forgot-password-phone", { phone });
      startCountdown();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resend OTP.");
    }
  };

  const inputClass = (hasError) =>
    `w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-colors duration-150 ${
      hasError ? "border-red-500" : "border-slate-300"
    }`;

  const backPath = urlLocketId ? `/${urlLocketId}/edit` : "/";

  const renderEmailForm = () => (
    <form onSubmit={handleSubmit(onSubmitEmail)}>
      {!urlLocketId && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            QR Code ID
          </label>
          <input
            type="text"
            {...register("locketId", {
              required: "QR Code ID is required",
              pattern: { value: /^[A-Z0-9]+$/i, message: "Letters and numbers only" },
            })}
            className={`${inputClass(errors.locketId)} uppercase`}
            placeholder="e.g. ABC123XY"
          />
          {errors.locketId && (
            <span className="block text-red-600 text-xs mt-1 font-medium">
              {errors.locketId.message}
            </span>
          )}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          Email Address
        </label>
        <input
          type="email"
          {...register("email", {
            required: "Email is required",
            pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" },
          })}
          className={inputClass(errors.email)}
          placeholder="your@email.com"
        />
        {errors.email && (
          <span className="block text-red-600 text-xs mt-1 font-medium">
            {errors.email.message}
          </span>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg text-base shadow-md transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed mb-4"
      >
        {isSubmitting ? "Sending..." : "Send Reset Link"}
      </button>
    </form>
  );

  const renderPhoneOtpForm = () => (
    <form onSubmit={handleSubmit(onSubmitPhoneOtp)}>
      <div className="mb-4">
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          Phone Number
        </label>
        <input
          type="tel"
          {...register("phone", {
            required: "Phone number is required",
            pattern: { value: /^[6-9]\d{9}$/, message: "Valid Indian 10-digit number required" },
          })}
          disabled={otpSent}
          className={`${inputClass(errors.phone)} ${otpSent ? "bg-slate-100" : "bg-white"}`}
          placeholder="10-digit mobile number"
          maxLength={10}
        />
        {errors.phone && (
          <span className="block text-red-600 text-xs mt-1 font-medium">
            {errors.phone.message}
          </span>
        )}
      </div>

      {otpSent && (
        <>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              OTP (sent to your phone)
            </label>
            <input
              type="text"
              {...register("otp", { required: "OTP is required", minLength: { value: 4, message: "Enter the OTP" } })}
              className={`${inputClass(errors.otp)} text-xl tracking-[6px] text-center`}
              placeholder="------"
              maxLength={6}
            />
            {errors.otp && (
              <span className="block text-red-600 text-xs mt-1 font-medium">
                {errors.otp.message}
              </span>
            )}
            <div className="flex justify-end mt-1">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={otpCountdown > 0}
                className={`bg-transparent border-none text-[13px] font-semibold p-0 ${
                  otpCountdown > 0 ? "text-slate-400 cursor-not-allowed" : "text-red-600 cursor-pointer hover:text-red-700"
                }`}
              >
                {otpCountdown > 0 ? `Resend OTP in ${otpCountdown}s` : "Resend OTP"}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              New Password
            </label>
            <input
              type="password"
              {...register("newPassword", {
                required: "Password is required",
                minLength: { value: 8, message: "Minimum 8 characters" },
                validate: {
                  hasUpper: (v) => /[A-Z]/.test(v) || "Must have an uppercase letter",
                  hasLower: (v) => /[a-z]/.test(v) || "Must have a lowercase letter",
                  hasNumber: (v) => /[0-9]/.test(v) || "Must have a number",
                },
              })}
              className={inputClass(errors.newPassword)}
              placeholder="New password (8+ chars)"
            />
            {errors.newPassword && (
              <span className="block text-red-600 text-xs mt-1 font-medium">
                {errors.newPassword.message}
              </span>
            )}
          </div>
        </>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg text-base shadow-md transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed mb-4"
      >
        {isSubmitting ? "Please wait..." : otpSent ? "Reset Password" : "Send OTP"}
      </button>
    </form>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-lg mx-auto px-3 py-2 flex items-center justify-between">
          <BrandLogo logoClassName="h-7" />
          <button
            onClick={() => navigate(backPath)}
            className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-150"
            title="Go back"
          >
            <ArrowLeft size={16} className="text-slate-700" />
          </button>
        </div>
      </div>

      <div className="flex flex-col items-center p-4 pt-8">
        <div className="w-full max-w-sm">
          {urlLocketId && (
            <div className="text-center mb-3">
              <div className="inline-block bg-slate-100 px-4 py-1.5 rounded-lg border border-slate-200">
                <span className="text-xs font-bold text-slate-500">QR CODE ID:</span>{" "}
                <span className="text-sm font-bold text-red-600">{urlLocketId}</span>
              </div>
            </div>
          )}

          <h2 className="text-xl font-extrabold text-slate-900 mb-1 text-center">
            {t("forgotPassword.title")}
          </h2>

          <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-4 mt-3">
            {[
              { key: "email", label: t("forgotPassword.emailTab") },
              ...(smsAvailable ? [{ key: "phone", label: t("forgotPassword.phoneTab") }] : []),
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => { setResetMethod(m.key); setOtpSent(false); setError(null); resetForm({ locketId: urlLocketId || "" }); }}
                className={`flex-1 py-2.5 text-sm font-semibold border-none cursor-pointer transition-colors duration-150 ${
                  resetMethod === m.key
                    ? "bg-red-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {successMessage ? (
            <div>
              <div className="bg-green-50 border border-green-300 rounded-lg p-5 mb-4 text-center">
                <p className="text-green-700 text-sm font-semibold leading-relaxed m-0">{successMessage}</p>
              </div>
              <Link to={backPath} className="no-underline">
                <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg text-base transition-colors duration-150">
                  &larr; Go Back
                </button>
              </Link>
            </div>
          ) : (
            <div>
              <p className="text-xs text-slate-500 text-center mb-4">
                {resetMethod === "phone"
                  ? otpSent ? "Enter the OTP sent to your phone and your new password" : "Enter your registered phone number to receive an OTP"
                  : `Enter ${urlLocketId ? "the" : "the QR code ID and"} email you used during setup`}
              </p>

              {resetMethod === "email" ? renderEmailForm() : renderPhoneOtpForm()}

              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-4 text-center">
                <p className="text-xs text-slate-600 m-0">
                  Need help? Contact us at{" "}
                  <a href="mailto:contact@dityatechnologies.in" className="text-red-600 no-underline font-semibold">
                    contact@dityatechnologies.in
                  </a>
                </p>
              </div>

              <div className="text-center">
                <Link to={backPath} className="text-slate-500 text-sm font-semibold no-underline p-1.5 inline-block hover:text-slate-700">
                  &larr; Go Back
                </Link>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 mt-4">
                  <p className="text-red-600 m-0 text-sm font-semibold">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Language switcher */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center gap-1">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
